<template>
  <div class="container">
    <div class="columns">
      <div class="column is-12">
        <h4 class="page-header">
          <b-icon icon="account"></b-icon>
          <span v-if="form.id">{{ form.name }}</span>
          <span v-if="!form.id">{{ $ml.get('titles_accounts_new_account') }}</span>
        </h4>
      </div>
    </div>
    <form @submit.prevent="submit" novalidate>
      <b-loading :is-full-page="false" v-model="loading" :can-cancel="false"></b-loading>

      <div class="columns">
        <div class="column">
          <b-tabs v-model="active_tab" type="is-boxed">
            <b-tab-item value="data" :label="$ml.get('accounts_account')" icon="account">
              <div class="columns">
                <div class="column is-6">
                  <b-field
                      :label="$ml.get('accounts_name') + ' *'"
                      :message="'name' in errors ? errors['name'] : $ml.get('accounts_name_placeholder')"
                      :type="'name' in errors ? 'is-danger' : ''" class="plate">
                    <b-input v-model="form.name" :placeholder="$ml.get('accounts_name_example')"
                             icon="account"></b-input>
                  </b-field>
                </div>
                <div class="column is-3">
                  <b-field
                      :label="$ml.get('accounts_bookmaker') + ' * (' + $ml.get('your_payments') + ': '
                      + this.total_payments + ')'"
                      :message="'bookmaker' in errors ? errors['bookmaker'] : $ml.get('accounts_bookmaker_placeholder')"
                      :type="'bookmaker' in errors ? 'is-danger' : ''" class="plate">
                    <b-select v-model="form.bookmaker" icon="tag" expanded :disabled="form.id !== null">
                      <option v-for="option in bookmakers" :value="option" :key="option"
                              :style="{ color: getColor(option) }">
                        {{ option }}
                      </option>
                    </b-select>
                  </b-field>
                  <div v-if="showVipWarning" class="warning">
                    Warning: {{ vipWarningMessage }}!
                  </div>
                </div>
                <div class="column is-3">
                  <b-field
                      :label="$ml.get('accounts_bk_mirror') + ' *'"
                      :message="'bk_mirror' in errors ? errors['bk_mirror'] : ''"
                      :type="'bk_mirror' in errors ? 'is-danger' : ''" class="plate">
                    <b-select v-model="form.bk_mirror" icon="earth" expanded>
                      <option value="">-</option>
                      <option v-for="option in mirrors" :value="option" :key="option">
                        {{ option }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column is-2">
                  <b-field
                      :label="$ml.get('accounts_currency') + ' *'"
                      :message="'currency' in errors ? errors['currency'] : $ml.get('accounts_currency_placeholder')"
                      :type="'currency' in errors ? 'is-danger' : ''" class="plate">
                    <b-select v-model="form.currency" icon="currency-usd-circle" expanded>
                      <option v-for="option in currencies" :value="option" :key="option">
                        {{ option }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-3">
                  <b-field
                      :label="$ml.get('accounts_login') + ' *'"
                      :message="'login' in errors ? errors['login'] : $ml.get('accounts_login_placeholder')"
                      :type="'login' in errors ? 'is-danger' : ''" class="plate">
                    <b-input v-model="form.login" icon="account"></b-input>
                  </b-field>
                </div>
                <div class="column is-3">
                  <b-field
                      :label="$ml.get('accounts_password') + ' *'"
                      :message="'password' in errors ? errors['password'] : $ml.get('accounts_password_placeholder')"
                      :type="'password' in errors ? 'is-danger' : ''" class="plate">
                    <b-input v-model="form.password" icon="lock"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column is-12">
                  <b-field
                      :label="$ml.get('accounts_comment')"
                      :message="'comment' in errors ? errors['comment'] : $ml.get('accounts_comment_placeholder')"
                      :type="'comment' in errors ? 'is-danger' : ''" class="plate">
                    <b-input size="is-small" type="textarea" v-model="form.comment"></b-input>
                  </b-field>
                </div>
              </div>
            </b-tab-item>
            <b-tab-item value="strategies" :label="$ml.get('accounts_strategies')" icon="briefcase">
              <div class="columns is-centered">
                <div class="column">
                  <b-message
                      title=""
                      type="is-info"
                      has-icon
                      aria-close-label="Close message">
                    {{ $ml.get('accounts_strategies_info') }}
                  </b-message>
                </div>
              </div>

              <div v-for="(strategy, ix) in form.strategies" class="box">
                <div class="columns">
                  <div class="column is-5">
                    <b-field :label="$ml.get('accounts_strategy') + ' *'" class="plate">
                      <b-select v-model="strategy.strategy_id" icon="briefcase" expanded
                                @change.native="updateTimetable(0)">
                        <option v-for="option in strategies" :value="option.id" :key="option.id"
                                v-if="option.bookmaker === form.bookmaker">
                          {{ option.name }} (ROI: {{ option.roi }}%)
                        </option>
                      </b-select>
                    </b-field>
                  </div>
                  <div class="column is-2">
                    <b-field
                        :label="$ml.get('accounts_stake') + ' *'" class="plate"
                        :message="'strategies' in errors && ix in errors['strategies'] && 'stake1' in errors['strategies'][ix] ? errors['strategies'][ix]['stake1'] : ''"
                        :type="'strategies' in errors && ix in errors['strategies'] && 'stake1' in errors['strategies'][ix] ? 'is-danger' : ''">
                      <b-input v-model="strategy.stake1" icon="currency-usd-circle"
                               :disabled="strategy.dynamic_stake"></b-input>
                    </b-field>
                  </div>
                </div>
                <div class="column is-1">
                  <b-button size="is-small" type="is-danger" outlined
                            @click="removeStrategy(ix)" :title="$ml.get('common_delete')">
                    <b-icon icon="delete" size="is-small"></b-icon>
                  </b-button>
                </div>
              </div>
              <div class="columns">
                <div class="column is-12 leftal">
                  <b-button :disabled="form.strategies.length >= 5"
                            @click="addStrategy()"
                            type="is-success">
                    <b-icon icon="plus" size="is-small"></b-icon>
                    <span>{{ $ml.get('accounts_add_strategy') }}</span>
                  </b-button>
                </div>
              </div>
            </b-tab-item>
          </b-tabs>
        </div>
      </div>
      <div class="columns">
        <div class="column is-12">
          <b-button
            :disabled="!isValid"
            type="is-info"
            native-type="submit"
          >
            <span class="custom-save-text">
              {{ $ml.get('common_save') }}
            </span>
          </b-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Vue from "vue";
import router from "@/router";
import {mapGetters} from 'vuex'
import Multiselect from "vue-multiselect";
import Sport from "@/components/Sport";

export default {
  name: "Create",
  data() {
    return {
      form: {
        id: null,
        name: '',
        currency: '',
        login: '',
        bookmaker: '',
        bk_mirror: '',
        password: '',
        proxy_id: null,
        comment: '',
        balance_deposit_notification: null,
        balance_withdraw_notification: null,
        manual_timetable: 0,
        leagues_include: 0,
        settings: {},
        leagues: [],
        strategies: [
          {
            strategy_id: '',
            stake1: '',
            stake2: '',
            stake3: '',
            dynamic_stake: false,
            dynamic_stake_percent: null,
            bets_per_event: 1,
            bets_interval: 120,
          }
        ],
        timetable: (() => {
          let res = {};
          for (let i = 1; i <= 7; i++) {
            res[i] = {};
            for (let j = 1; j <= 24; j++) {
              res[i][j] = true;
            }
          }
          return res;
        })(),
      },
      loading: false,
      timetable_loading: false,
      load_settings: null,
      errors: {},
      search_league: '',
      leagues_list: [],
      showVipWarning: false,
      vipWarningMessage: '',
      total_payments: 0,
      bookies: {},
    }
  },
  components: {
    Multiselect, Sport
  },
  computed: {
    ...mapGetters('data', ['strategies', 'bookmakers', 'currencies', 'sports', 'markets', 'bk_mirrors',]),
    active_tab: {
      get() {
        let def = 'data'
        if ('strategies' in this.errors) {
          def = 'strategies'
        } else if (
            'balance_deposit_notification' in this.errors || 'balance_withdraw_notification' in this.errors ||
            'bets_per_event' in this.errors || 'bets_interval' in this.errors || 'sports' in this.errors
        ) {
          def = 'settings'
        }
        return def
      },
      set() {
      }
    },
    sport_tab: {
      get() {
        return undefined
      },
      set() {
      }
    },
    isValid() {
      let valid = (this.form.name.length > 0 && this.form.login.length > 0 && this.form.password.length > 0 &&
          this.form.bookmaker.length > 0 && this.form.currency.length > 0 && this.form.strategies.length > 0)

      return valid
    },
    proxies() {
      return (this.form.bookmaker.includes('BET365')) ? this.$store.getters['data/mobileProxies'] : this.$store.getters['data/proxies']
    },
    rooms() {
      return this.$store.getters['data/myRooms']
    },
    mirrors() {
      if (this.form.bookmaker in this.bk_mirrors) {
        return this.bk_mirrors[this.form.bookmaker]
      }
      return [];
    }
  },
  watch: {
    search_league: function (val, oldVal) {
      if (val !== oldVal && val.length >= 3) {
        Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `rooms/get_leagues?q=${val}`).then((resp) => {
          this.leagues_list = resp.data.result
        })
      }
    },
    'form.bookmaker'(newVal) {
      if (newVal && this.bookies[newVal] && parseFloat(this.bookies[newVal]) > parseFloat(this.total_payments)) {
        this.showVipWarning = true;
        this.vipWarningMessage = `${newVal} ${this.$ml.get('vip_warning')} ${this.bookies[newVal]} EURO`;
      } else if (newVal) {
        this.showVipWarning = false;
        this.vipWarningMessage = '';
      }
    }
  },
  methods: {
    addLeague: function (league) {
      if (!this.form.leagues.includes(league)) {
        this.form.leagues.push(league)
      }
    },
    removeLeague: function (league) {
      this.form.leagues = this.form.leagues.filter(item => item !== league)
    },
    loadSettings: function () {
      this.loading = true
      Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `rooms/get_settings?room_id=${this.load_settings}`).then((resp) => {
        Object.keys(resp.data.result).forEach(key => {
          this.form[key] = resp.data.result[key]
        });
      }).catch(err => {
        this.flashMessage.error({
          title: this.$ml.get('common_request_failed'),
          message: this.$ml.get('common_request_failed_message'),
        })
      }).finally(() => {
        this.loading = false
      })
    },
    switchSchedule: function (day, hour) {
      if (this.form.manual_timetable) {
        this.form.timetable[day][hour] = !this.form.timetable[day][hour]
      }
    },
    addStrategy: function () {
      this.form.strategies.push({
        strategy_id: '',
        stake1: '',
        stake2: '',
        stake3: '',
        dynamic_stake: false,
        dynamic_stake_percent: null,
        bets_per_event: 1,
        bets_interval: 120,
      })
    },
    removeStrategy: function (i) {
      this.form.strategies.splice(i, 1)
      this.updateTimetable(0)
    },
    resetTimetable: function () {
      if (confirm(this.$ml.get('common_confirm'))) {
        this.updateTimetable(1)
      }
    },
    updateTimetable: function (force) {
      if (force === 1 || !this.form.manual_timetable) {
        this.timetable_loading = true
        let ids = [];
        this.form.strategies.forEach((item, i, arr) => {
          ids.push(item.strategy_id)
        })
        Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `rooms/build_timetable?s=${ids.join(',')}`).then((resp) => {
          this.form.timetable = resp.data.result
        }).catch(err => {
          this.flashMessage.error({
            title: this.$ml.get('common_request_failed'),
            message: this.$ml.get('common_request_failed_message'),
          })
        }).finally(() => {
          this.timetable_loading = false
        })
      }
    },
    submit: function () {
      this.loading = true
      this.errors = {}
      Vue.prototype.$http.post(process.env.VUE_APP_API_HOST + 'rooms/index', {room: this.form}).then(resp => {
        this.flashMessage.success({
          title: this.$ml.get('common_request_success'),
          message: this.$ml.get('common_request_success_data_message'),
        })
        if (!this.form.id) {
          router.push('/rooms')
        }
      }).catch(err => {
        if (err.response.status === 400) {
          this.errors = err.response.data.result
        } else {
          this.flashMessage.error({
            title: this.$ml.get('common_request_failed'),
            message: this.$ml.get('common_request_failed_message'),
          })
        }
      }).finally(() => {
        this.loading = false
      })
    },
    getColor(option) {
      if (this.bookies[option] && this.bookies[option] > this.total_payments) {
        return '#b7adad';
      } else {
        return '#4a4a4a';
      }
    }
  },
  beforeMount() {
    this.$store.dispatch('data/strategies')
    this.$store.dispatch('data/proxies')
    this.$store.dispatch('data/myRooms')
    this.sports.forEach((sport) => {
      this.form.settings[sport] = {
        active: true,
        coef_from: 1.5,
        coef_to: 2.4,
        full_time_only: false,
        not_bet_junior: false,
        not_bet_woman: false,
        markets: {
          ONE_TWO: {
            ONE: {
              active: true,
            },
            TWO: {
              active: true,
            },
            ONE_TWO: {
              active: true,
            },
            DRAW: {
              active: true,
            },
            ONE_DRAW: {
              active: true,
            },
            TWO_DRAW: {
              active: true,
            },
          },
          TOTAL: {
            UNDER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            OVER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
          },
          T1_TOTAL: {
            UNDER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            OVER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
          },
          T2_TOTAL: {
            UNDER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            OVER: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
          },
          HDP: {
            HOME: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            AWAY: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
          },
          EURO_HDP: {
            H1: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            H2: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
            HX: {
              active: true,
              pivot_from: null,
              pivot_to: null,
            },
          },
        },
      };
      if (sport === 'FOOTBALL') {
        this.form.settings[sport]['markets']['CORNER_TOTAL'] = {
          UNDER: {
            active: true,
            pivot_from: null,
            pivot_to: null,
          },
          OVER: {
            active: true,
            pivot_from: null,
            pivot_to: null,
          },
        }
        this.form.settings[sport]['markets']['CORNER_HDP'] = {
          HOME: {
            active: true,
            pivot_from: null,
            pivot_to: null,
          },
          AWAY: {
            active: true,
            pivot_from: null,
            pivot_to: null,
          },
        }
      }
    })
    let id = this.$route.query.id
    if (id) {
      this.loading = true
      Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `rooms/index?id=${id}`).then((resp) => {
        this.form = resp.data.result
      }).catch(err => {
        this.flashMessage.error({
          title: this.$ml.get('common_request_failed'),
          message: this.$ml.get('common_request_failed_message'),
        })
      }).finally(() => {
        this.loading = false
      })
    }
  },
  metaInfo() {
    return {
      title: this.form.name ? this.form.name : this.$ml.get('titles_accounts_new_account')
    }
  },
  mounted() {
    Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `profile/payments`).then(resp => {
      this.total_payments = resp.data.result.totalPayments;
    });
    Vue.prototype.$http.get(process.env.VUE_APP_API_HOST + `bookies/bookies`).then(resp => {
      this.bookies = resp.data.result.bookies;
    });
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
table.schedule-table {
  table-layout: fixed;
  width: 100%;
}

table.schedule-table td, table.schedule-table th {
  padding: 3px;
}

table.schedule-table td.bot-schedule {
  cursor: pointer;
}

.bot-schedule.enabled {
  background-color: rgba(92, 184, 92, 0.7);
}

.bot-schedule.disabled {
  background-color: lightgray;
}

.market-cont {
  border: solid #8888;
  border-width: 0 0 0 1px;
}

.market-cont .market-name {
  padding: 5px;
  margin: -10px 0 0 -10px;
  border: 1px solid #8888;
  width: fit-content;
  background-color: white;
}

.market-cont .market-body {
  display: flex;
  align-items: center;
}

.market-cont .market-body .field {
  margin: 5px;
}

.markets-list {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.pivot-input {
  width: 70px;
}

.search-results {
  display: flex;
  justify-content: center;
}

.search-results ul {
  width: 100%;
  padding: 6px;
}

.search-results li {
  padding: 5px;
  border-bottom: 1px dotted;
  display: flex;
  align-items: center;
}

.search-results li:hover {
  background-color: rgba(221, 221, 221, .5);
  cursor: pointer;
}

.search-results li span {
  display: inline-block;
  margin-left: 20px;
}

.warning {
  color: red;
  font-weight: bold;
  margin-top: 10px;
}

.custom-save-text {
  color: white;
  font-weight: bold;
}
</style>
